// src/scripts/useScripts/useRota.js
import { ref, computed } from "vue";
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";

import { MAX_WEEKS_FORWARD } from "@/scripts/sharedHelpers/weeklyLimits";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toYmd(d) {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
}

function fromYmd(ymd) {
  return new Date(`${ymd}T00:00:00`);
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function addWeeks(date, n) {
  return addDays(date, n * 7);
}

function startOfWeekMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  return addDays(d, diff);
}

function endOfWeekSunday(weekStartMonday) {
  return addDays(weekStartMonday, 6);
}

function formatUkShort(ymd) {
  return fromYmd(ymd).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
}

function formatTimeLabel(hhmm) {
  const [hStr, mStr] = String(hhmm || "00:00").split(":");
  let h = Number(hStr);
  const m = Number(mStr || 0);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return m === 0 ? `${h} ${ampm}` : `${h}:${pad2(m)} ${ampm}`;
}

function minutesBetween(startHHMM, endHHMM) {
  const [sh, sm] = String(startHHMM || "00:00").split(":").map(Number);
  const [eh, em] = String(endHHMM || "00:00").split(":").map(Number);

  const start = sh * 60 + sm;
  let end = eh * 60 + em;

  // overnight shift, e.g. 23:00 -> 03:00
  if (end <= start) end += 24 * 60;

  return end - start;
}

function hoursDecimalFromShift(shift) {
  const mins = minutesBetween(shift.startTime, shift.endTime);
  const breakMins = Number(shift.breakMinutes || 0);
  return Math.max(0, (mins - breakMins) / 60);
}

function buildWeekStartsForRange(startYmd, endYmd) {
  const start = startOfWeekMonday(fromYmd(startYmd));
  const end = startOfWeekMonday(fromYmd(endYmd));

  const weekStarts = [];
  let cursor = new Date(start);

  while (cursor <= end) {
    weekStarts.push(toYmd(cursor));
    cursor = addWeeks(cursor, 1);
  }

  return weekStarts;
}

export default function useRota() {
  const db = getFirestore();

  const viewWeekStart = ref(toYmd(startOfWeekMonday(new Date())));
  const loading = ref(false);
  const error = ref("");
  const staff = ref([]);
  const shifts = ref([]);
  const rotaMeta = ref(null);

  const weekId = computed(() => viewWeekStart.value);

  const currentWeekStart = computed(() => {
    return toYmd(startOfWeekMonday(new Date()));
  });

  const maxWeekStart = computed(() => {
    return toYmd(
      addWeeks(
        fromYmd(currentWeekStart.value),
        Number(MAX_WEEKS_FORWARD || 0)
      )
    );
  });

  const canGoNext = computed(() => {
    return toYmd(addWeeks(fromYmd(viewWeekStart.value), 1)) <= maxWeekStart.value;
  });

  const canAddOrEdit = computed(() => {
    return viewWeekStart.value <= maxWeekStart.value;
  });

  const viewWeekEnd = computed(() => {
    return toYmd(endOfWeekSunday(fromYmd(viewWeekStart.value)));
  });

  const weekDays = computed(() => {
    return Array.from({ length: 7 }).map((_, i) => {
      const ymd = toYmd(addDays(fromYmd(viewWeekStart.value), i));
      return {
        ymd,
        label: formatUkShort(ymd),
      };
    });
  });

  const weekRangeText = computed(() => {
    const s = fromYmd(viewWeekStart.value).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    });

    const e = fromYmd(viewWeekEnd.value).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    });

    return `${s} – ${e}`;
  });

  function setWeekStart(weekStartYmd) {
    viewWeekStart.value = weekStartYmd;
  }

  function prevWeek() {
    viewWeekStart.value = toYmd(addWeeks(fromYmd(viewWeekStart.value), -1));
  }

  function nextWeek() {
    if (canGoNext.value) {
      viewWeekStart.value = toYmd(addWeeks(fromYmd(viewWeekStart.value), 1));
    }
  }

  async function ensureRotaWeek(venueId) {
    const rotaRef = doc(db, `venues/${venueId}/rotas/${weekId.value}`);
    const snap = await getDoc(rotaRef);

    if (!snap.exists()) {
      await setDoc(rotaRef, {
        weekStart: viewWeekStart.value,
        weekEnd: viewWeekEnd.value,
        status: "draft",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      rotaMeta.value = {
        id: weekId.value,
        weekStart: viewWeekStart.value,
        weekEnd: viewWeekEnd.value,
        status: "draft",
      };
    } else {
      rotaMeta.value = {
        id: snap.id,
        ...(snap.data() || {}),
      };
    }
  }

  // Pure Firestore staff loader used by rota editing screens.
  async function loadStaff(venueId) {
    if (!venueId) return;

    try {
      const snap = await getDocs(
        query(
          collection(db, `venues/${venueId}/staff_details`),
          orderBy("staff_name", "asc")
        )
      );

      staff.value = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
    } catch (e) {
      console.warn("[STAFF] loadStaff failed:", e.message);
      staff.value = [];
    }
  }

  async function loadShifts(venueId) {
    const snap = await getDocs(
      query(
        collection(db, `venues/${venueId}/rotas/${weekId.value}/shifts`),
        orderBy("date", "asc"),
        orderBy("startTime", "asc")
      )
    );

    shifts.value = snap.docs.map((d) => ({
      id: d.id,
      rotaWeekId: weekId.value,
      ...d.data(),
    }));
  }

  async function loadShiftsForDateRange(venueId, startYmd, endYmd) {
    const cleanStart = String(startYmd || "").trim();
    const cleanEnd = String(endYmd || "").trim();

    if (!venueId) return;
    if (!cleanStart || !cleanEnd) return;
    if (cleanStart > cleanEnd) {
      shifts.value = [];
      return;
    }

    const weekStarts = buildWeekStartsForRange(cleanStart, cleanEnd);
    const allShifts = [];

    for (const rotaWeekId of weekStarts) {
      try {
        const snap = await getDocs(
          query(
            collection(db, `venues/${venueId}/rotas/${rotaWeekId}/shifts`),
            orderBy("date", "asc"),
            orderBy("startTime", "asc")
          )
        );

        snap.docs.forEach((d) => {
          const row = {
            id: d.id,
            rotaWeekId,
            ...d.data(),
          };

          if (row.date >= cleanStart && row.date <= cleanEnd) {
            allShifts.push(row);
          }
        });
      } catch (e) {
        console.warn("[ROTA] loadShiftsForDateRange week failed:", {
          rotaWeekId,
          message: e?.message || String(e),
        });
      }
    }

    allShifts.sort((a, b) => {
      const dateCompare = String(a.date || "").localeCompare(String(b.date || ""));
      if (dateCompare !== 0) return dateCompare;

      return String(a.startTime || "").localeCompare(String(b.startTime || ""));
    });

    shifts.value = allShifts;
  }

  async function loadWeek(venueId) {
    if (!venueId) return;

    loading.value = true;
    error.value = "";

    try {
      await ensureRotaWeek(venueId);
      await Promise.all([loadStaff(venueId), loadShifts(venueId)]);
    } catch (e) {
      error.value = e?.message || String(e);
      console.warn("[ROTA] loadWeek error:", error.value);
    } finally {
      loading.value = false;
    }
  }

  async function loadDateRange(venueId, startYmd, endYmd) {
    if (!venueId) return;

    loading.value = true;
    error.value = "";

    try {
      await Promise.all([
        loadStaff(venueId),
        loadShiftsForDateRange(venueId, startYmd, endYmd),
      ]);

      rotaMeta.value = {
        id: `${startYmd}_${endYmd}`,
        weekStart: startYmd,
        weekEnd: endYmd,
        status: "date-range",
      };
    } catch (e) {
      error.value = e?.message || String(e);
      console.warn("[ROTA] loadDateRange error:", error.value);
    } finally {
      loading.value = false;
    }
  }

  function shiftsForCell(staffId, ymd) {
    return shifts.value.filter((s) => s.staffId === staffId && s.date === ymd);
  }

  function shiftHours(shift) {
    return hoursDecimalFromShift(shift);
  }

  function staffHoursForWeek(staffId) {
    return shifts.value
      .filter((s) => s.staffId === staffId)
      .reduce((sum, s) => sum + shiftHours(s), 0);
  }

  function dayShiftCount(ymd) {
    return shifts.value.filter((s) => s.date === ymd).length;
  }

  function dayHoursTotal(ymd) {
    return shifts.value
      .filter((s) => s.date === ymd)
      .reduce((sum, s) => sum + shiftHours(s), 0);
  }

  async function createOrUpdateShift(venueId, payload) {
    if (!venueId) throw new Error("Missing venueId");

    if (!canAddOrEdit.value) {
      throw new Error(
        `Cannot edit rotas beyond current week + ${Number(
          MAX_WEEKS_FORWARD || 0
        )}.`
      );
    }

    if (payload.date < viewWeekStart.value || payload.date > viewWeekEnd.value) {
      throw new Error("Shift date must be within the selected week.");
    }

    const base = {
      staffId: String(payload.staffId || "").trim(),
      staffNameSnapshot: String(payload.staffNameSnapshot || "").trim(),
      jobTitleSnapshot: String(payload.jobTitleSnapshot || "").trim(),
      payRateSnapshot: Number(payload.payRateSnapshot || 0),
      date: payload.date,
      startTime: payload.startTime,
      endTime: payload.endTime,
      role: String(payload.role || "").trim(),
      breakMinutes: Number(payload.breakMinutes || 0),
      notes: String(payload.notes || "").trim(),
      updatedAt: serverTimestamp(),
    };

    if (!base.staffId) throw new Error("Missing staffId");
    if (!base.date) throw new Error("Missing date");
    if (!base.startTime || !base.endTime) {
      throw new Error("Start/end time required");
    }

    const shiftsRef = collection(
      db,
      `venues/${venueId}/rotas/${weekId.value}/shifts`
    );

    if (payload.id) {
      await updateDoc(
        doc(db, `venues/${venueId}/rotas/${weekId.value}/shifts/${payload.id}`),
        base
      );
    } else {
      await addDoc(shiftsRef, {
        ...base,
        createdAt: serverTimestamp(),
      });
    }

    await updateDoc(doc(db, `venues/${venueId}/rotas/${weekId.value}`), {
      updatedAt: serverTimestamp(),
    });

    await loadShifts(venueId);
  }

  async function deleteShift(venueId, shiftId) {
    if (!venueId) throw new Error("Missing venueId");

    if (!canAddOrEdit.value) {
      throw new Error(
        `Cannot edit rotas beyond current week + ${Number(
          MAX_WEEKS_FORWARD || 0
        )}.`
      );
    }

    await deleteDoc(
      doc(db, `venues/${venueId}/rotas/${weekId.value}/shifts/${shiftId}`)
    );

    await updateDoc(doc(db, `venues/${venueId}/rotas/${weekId.value}`), {
      updatedAt: serverTimestamp(),
    });

    await loadShifts(venueId);
  }

  function makeDefaultShiftDraft({ staffRow, ymd }) {
    return {
      id: null,
      staffId: staffRow.id,
      staffNameSnapshot: staffRow.staff_name || staffRow.name || "—",
      jobTitleSnapshot: staffRow.job_title || staffRow.jobTitle || "",
      payRateSnapshot: Number(staffRow.pay_rate ?? staffRow.payRate ?? 0),
      date: ymd,
      startTime: "15:00",
      endTime: "23:00",
      role: staffRow.job_title || staffRow.jobTitle || "Shift",
      breakMinutes: 0,
      notes: "",
    };
  }

  return {
    loading,
    error,
    staff,
    shifts,
    rotaMeta,

    weekId,
    viewWeekStart,
    viewWeekEnd,
    weekDays,
    weekRangeText,

    currentWeekStart,
    maxWeekStart,
    canGoNext,
    canAddOrEdit,

    setWeekStart,
    prevWeek,
    nextWeek,

    shiftsForCell,
    staffHoursForWeek,
    dayShiftCount,
    dayHoursTotal,
    shiftHours,
    formatTimeLabel,

    loadWeek,
    loadStaff,
    loadShifts,
    loadDateRange,
    loadShiftsForDateRange,

    createOrUpdateShift,
    deleteShift,
    makeDefaultShiftDraft,
  };
}